import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { Loader2 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Alert, AlertDescription } from '../ui/alert';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import { Select } from '../ui/newSelect';
import { Label } from '../ui/label';
import BrandSelect from '../ui/BrandSelect';
import Breadcrumb from '../Common/Breadcrumb';
import { adminToolbarSelect } from '../utils/ReactSelectStyles';
import { Actions as ADMINACTIONS } from '../../store/actions/adminActions';
import { Actions as COMMERCIAL } from '../../store/actions/commercialActions';
import { toggleAlertMessage } from '../../store/action';

const ERRORS = {
  partner_agreement_missing: 'This Partner has no accepted Partner Reseller Agreement yet.',
  partner_price_not_accepted: 'That Plan and FTE band is not in the accepted Partner Account Fee schedule.',
  partner_addon_not_accepted: 'One of those Add-ons is not in the accepted Partner Account Fee schedule.',
  forbidden: 'You do not have permission to change this account.',
};

const titleCase = (value) =>
  String(value || '')
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, (letter) => letter.toUpperCase());

const money = (value, currency = 'GBP') =>
  value == null ? '—' : new Intl.NumberFormat('en-GB', { style: 'currency', currency }).format(Number(value));

/**
 * Monthly Partner Account Fee for one account: twelfth of the annual Plan fee plus non-included Add-ons.
 * Demo and inactive accounts pay nothing.
 * @param {object} feeSchedule accepted `partnerAccountFeeSchedule`
 * @param {{ planId?: string, fteBand?: string, addOnIds?: string[], demo?: boolean, status?: string }} account
 * @returns {number|null}
 */
export const monthlyAccountFee = (feeSchedule, account) => {
  if (!feeSchedule || account.demo || account.status !== 'Active') return 0;
  const plan = feeSchedule.plans?.[account.planId]?.[account.fteBand]?.fee;
  if (plan == null) return null;
  const addOns = (account.addOnIds || [])
    .map((id) => (feeSchedule.addOns || []).find((a) => a.id === id))
    .filter((a) => a && !(a.includedPlanIds || []).includes(account.planId))
    .reduce((sum, a) => sum + Number(a.fee || 0), 0);
  return Math.round(((Number(plan) + addOns) / 12) * 100) / 100;
};

const AccountRow = ({ account, feeSchedule, canSetDemo, onSavePlan, onSetDemo }) => {
  const [draft, setDraft] = useState({
    planId: account.planId || '',
    fteBand: account.fteBand || '',
    addOnIds: account.addOnIds || [],
  });
  const [busy, setBusy] = useState(false);

  const planOptions = useMemo(
    () => Object.keys(feeSchedule?.plans || {}).map((id) => ({ value: id, label: titleCase(id) })),
    [feeSchedule]
  );
  const bandOptions = useMemo(
    () => Object.keys(feeSchedule?.plans?.[draft.planId] || {}).map((id) => ({ value: id, label: `${id} FTE` })),
    [feeSchedule, draft.planId]
  );
  const addOnOptions = useMemo(
    () =>
      (feeSchedule?.addOns || [])
        .filter((a) => !(a.includedPlanIds || []).includes(draft.planId))
        .map((a) => ({ value: a.id, label: a.name || titleCase(a.id) })),
    [feeSchedule, draft.planId]
  );

  const dirty =
    draft.planId !== (account.planId || '') ||
    draft.fteBand !== (account.fteBand || '') ||
    JSON.stringify(draft.addOnIds) !== JSON.stringify(account.addOnIds || []);

  const save = async () => {
    setBusy(true);
    await onSavePlan(account.id, draft);
    setBusy(false);
  };

  const fee = monthlyAccountFee(feeSchedule, account);

  return (
    <TableRow className='border-border align-top'>
      <TableCell className='font-medium'>
        {account.name || account.id}
        <div className='flex gap-1 mt-1'>
          {account.status !== 'Active' && <Badge variant='secondary'>Inactive</Badge>}
          {account.demo && <Badge variant='outline'>Demo</Badge>}
        </div>
      </TableCell>
      <TableCell className='min-w-40'>
        <Select
          value={draft.planId}
          onChange={(v) => setDraft((d) => ({ ...d, planId: v || '', fteBand: '' }))}
          options={planOptions}
          placeholder='Plan'
        />
      </TableCell>
      <TableCell className='min-w-32'>
        <Select
          value={draft.fteBand}
          onChange={(v) => setDraft((d) => ({ ...d, fteBand: v || '' }))}
          options={bandOptions}
          placeholder='FTE band'
          disabled={!draft.planId}
        />
      </TableCell>
      <TableCell className='min-w-48'>
        <Select
          multiple
          value={draft.addOnIds}
          onChange={(v) => setDraft((d) => ({ ...d, addOnIds: v || [] }))}
          options={addOnOptions}
          placeholder='None'
          closeOnSelect={false}
        />
      </TableCell>
      <TableCell className='text-right whitespace-nowrap'>
        {fee == null ? <span className='text-warning'>Not priced</span> : money(fee, feeSchedule?.currency)}
      </TableCell>
      {canSetDemo && (
        <TableCell>
          <Switch checked={!!account.demo} onCheckedChange={(v) => onSetDemo(account.id, v)} />
        </TableCell>
      )}
      <TableCell>
        <Button size='sm' onClick={save} loading={busy} disabled={!dirty || !draft.planId || !draft.fteBand}>
          Save
        </Button>
      </TableCell>
    </TableRow>
  );
};

/**
 * Partner client accounts (R-7028). A Partner Admin sets each client's Plan, FTE band and Add-ons
 * from the accepted Partner Account Fee schedule; only a Platform Admin sets the Demo flag.
 */
const PartnerAccounts = ({
  cyfAdmin,
  adminOrgs,
  getAdminOrganisations,
  listPartnerAccounts,
  setPartnerAccountPlan,
  setDemoFlag,
  showAlert,
  embedded = false,
}) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!cyfAdmin);
  const [error, setError] = useState(null);
  const [partner, setPartner] = useState(null);

  const partnerOptions = useMemo(
    () =>
      (adminOrgs || [])
        .filter((o) => o.partnerResellerContractId || String(o.accountType || '').startsWith('White Label'))
        .map((o) => ({ value: o.id, label: o.name || o.id })),
    [adminOrgs]
  );

  useEffect(() => {
    if (cyfAdmin) getAdminOrganisations();
  }, [cyfAdmin, getAdminOrganisations]);

  const partnerOrgId = partner?.value;

  const reload = useCallback(async () => {
    if (cyfAdmin && !partnerOrgId) return;
    setLoading(true);
    const res = await listPartnerAccounts(partnerOrgId);
    setLoading(false);
    if (res.ok) {
      setData(res.data);
      setError(null);
    } else setError(ERRORS[res.error] || res.error);
  }, [cyfAdmin, partnerOrgId, listPartnerAccounts]);

  useEffect(() => {
    reload();
  }, [reload]);

  const onSavePlan = async (organisationId, plan) => {
    const res = await setPartnerAccountPlan(organisationId, plan);
    if (!res.ok) return showAlert(true, ERRORS[res.error] || res.error, 'error', 6000);
    showAlert(true, 'Plan saved. It applies from today on the next monthly Partner invoice.', 'success', 4000);
    reload();
  };

  const onSetDemo = async (organisationId, demo) => {
    const res = await setDemoFlag(organisationId, demo);
    if (!res.ok) return showAlert(true, ERRORS[res.error] || res.error, 'error', 6000);
    reload();
  };

  const accounts = data?.accounts || [];
  const feeSchedule = data?.feeSchedule;
  const total = accounts.reduce((sum, a) => sum + (monthlyAccountFee(feeSchedule, a) || 0), 0);

  return (
    <div className={embedded ? 'px-6 pb-6 space-y-4' : 'p-6 space-y-4'}>
      {!embedded && <Breadcrumb items={[{ label: 'Partner accounts', link: '/partner/accounts' }]} />}
      <Card className='bg-card text-card-foreground'>
        <CardHeader>
          <CardTitle>{embedded ? 'Client accounts' : 'Partner accounts'}</CardTitle>
        </CardHeader>
        <CardContent className='space-y-4'>
          <Alert>
            <AlertDescription className='text-xs'>
              Each active account is billed monthly at the Partner Account Fee for its Plan, FTE band and Add-ons.
              Changes apply from the day they are saved. Demo and inactive accounts are not charged.
            </AlertDescription>
          </Alert>
          {cyfAdmin && (
            <div className='max-w-md'>
              <Label>Partner</Label>
              <BrandSelect
                classNamePrefix='cyf-select'
                options={partnerOptions}
                value={partner}
                onChange={(v) => {
                  setPartner(v);
                  setData(null);
                }}
                isClearable
                isSearchable
                placeholder='Search Partners'
                styles={adminToolbarSelect}
                menuPosition='fixed'
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
              />
            </div>
          )}
          {cyfAdmin && !partnerOrgId ? (
            <p className='text-sm text-muted-foreground'>Choose a Partner to see its client accounts.</p>
          ) : loading ? (
            <div className='flex items-center gap-2 text-muted-foreground'>
              <Loader2 className='h-4 w-4 animate-spin' />
              Loading…
            </div>
          ) : error ? (
            <p className='text-sm text-destructive'>{error}</p>
          ) : !feeSchedule ? (
            <p className='text-sm text-muted-foreground'>{ERRORS.partner_agreement_missing}</p>
          ) : accounts.length === 0 ? (
            <p className='text-sm text-muted-foreground'>No client accounts yet.</p>
          ) : (
            <div className='overflow-x-auto rounded-md border border-border'>
              <Table>
                <TableHeader>
                  <TableRow className='border-border'>
                    <TableHead>Account</TableHead>
                    <TableHead>Plan</TableHead>
                    <TableHead>FTE</TableHead>
                    <TableHead>Add-ons</TableHead>
                    <TableHead className='text-right'>Monthly fee</TableHead>
                    {cyfAdmin && <TableHead>Demo</TableHead>}
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {accounts.map((account) => (
                    <AccountRow
                      key={`${account.id}-${account.planId}-${account.fteBand}-${(account.addOnIds || []).join(',')}`}
                      account={account}
                      feeSchedule={feeSchedule}
                      canSetDemo={!!cyfAdmin}
                      onSavePlan={onSavePlan}
                      onSetDemo={onSetDemo}
                    />
                  ))}
                  <TableRow className='border-border'>
                    <TableCell colSpan={4} className='font-semibold'>
                      Current monthly Partner Account Fees
                    </TableCell>
                    <TableCell className='text-right font-semibold'>{money(total, feeSchedule.currency)}</TableCell>
                    <TableCell colSpan={cyfAdmin ? 2 : 1} />
                  </TableRow>
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  cyfAdmin: state.profile?.details?.cyfAdmin,
  adminOrgs: state?.admin?.organisations,
});

const mapDispatchToProps = (dispatch) => ({
  getAdminOrganisations: () => dispatch(ADMINACTIONS.getAdminOrganisations({ includeAll: true })),
  listPartnerAccounts: (partnerOrgId) => dispatch(COMMERCIAL.listPartnerAccounts(partnerOrgId)),
  setPartnerAccountPlan: (id, plan) => dispatch(COMMERCIAL.setPartnerAccountPlan(id, plan)),
  setDemoFlag: (id, demo) => dispatch(COMMERCIAL.setDemoFlag(id, demo)),
  showAlert: (status, message, severity, hideDuration) =>
    dispatch(toggleAlertMessage(status, message, severity, hideDuration)),
});

export default connect(mapStateToProps, mapDispatchToProps)(PartnerAccounts);
